var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Expose, Type } from 'class-transformer';
import { GkvBillingRunFileStatus } from '../enums/gkv-billing-run-file-status.enum.js';
import { GkvBillingRunItemStatus } from '../enums/gkv-billing-run-item-status.enum.js';
import { GkvBillingRunStatus } from '../enums/gkv-billing-run-status.enum.js';
import { LieferungStatus } from '../enums/lieferung-status.enum.js';
import { SecondFactorType } from '../../auth/second-factor.js';
import { VersandOperatorActDto } from './versand-rueckmeldung-queue.dto.js';
/**
 * The Belegannahmestelle a Gesamtrechnung's Urbelege are posted to (#1553).
 *
 * `null` on the Gesamtrechnung where no in-force Kostenträgerdatei edition
 * publishes a `VKG 09` — 18 of the 958 card-IKs in the committed corpus, all
 * AOK Baden-Württemberg. Those Fälle are billed; the gap is shown as a gap.
 */
export class GkvPapierannahmestelleDto {
    /** Spec name: "Institutionskennzeichen". */
    ik;
    /** `NAM` lines 1–4, empty ones dropped — one address line each. */
    name;
    /** `1` Hausanschrift · `2` Postfach · `3` Großkunde. `null` where none was published. */
    addressType;
    postalCode;
    city;
    /** `null` on a Postfach-Anschrift. */
    street;
}
__decorate([
    Expose()
    /** Spec name: "Institutionskennzeichen". */
    ,
    __metadata("design:type", String)
], GkvPapierannahmestelleDto.prototype, "ik", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Array)
], GkvPapierannahmestelleDto.prototype, "name", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvPapierannahmestelleDto.prototype, "addressType", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvPapierannahmestelleDto.prototype, "postalCode", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvPapierannahmestelleDto.prototype, "city", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvPapierannahmestelleDto.prototype, "street", void 0);
/**
 * One Gesamtrechnung of a produced Nutzdatendatei (#1553).
 *
 * The level the Anlage-4-Begleitzettel is written at: one Karten-IK under one
 * Kostenträger, one Rechnungsnummer, one stack of Urbelege.
 *
 * ⚠ **Two Kassen and they are not interchangeable.** {@link kartenIk} is the
 * Krankenkasse the eGK names, {@link kostentraegerIk} the party that pays —
 * and for two of the six seeded Kassen the two names differ. A surface that
 * showed one of them would be picking a side in a question Anlage 4 answers.
 */
export class GkvBillingRunSummaryInvoiceDto {
    id;
    /** The burned §302 Rechnungsnummer — the raw run-up, never a formatted one. */
    rechnungsnummer;
    /** ISO timestamp — when the Nutzdatendatei carrying it was produced. */
    rechnungsdatum;
    /** `IK der Krankenkasse` off the eGK — Anlage 4 Feld 1. */
    kartenIk;
    /** `null` where the corpus publishes no name for that card-IK. */
    cardInsurerName;
    kostentraegerIk;
    kostentraegerName;
    leistungserbringerIk;
    papierannahmestelle;
    /** Anlage 4 Feld 7 — how many Urbelege lie under this Begleitzettel. */
    urbelegCount;
    /**
     * This Gesamtrechnung's Begleitzettel PDF, or `null` (TM-9540 / #1554).
     *
     * ⚠ **`null` means *"there is none"*, not *"not yet"*.** The pointer is
     * recorded once the PDF has come into being; several Gesamtrechnungen of one
     * Belegannahmestelle point at the same document, because one PDF comes into
     * being per Annahmestelle.
     */
    begleitzettelDocumentId;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingRunSummaryInvoiceDto.prototype, "id", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvBillingRunSummaryInvoiceDto.prototype, "rechnungsnummer", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingRunSummaryInvoiceDto.prototype, "rechnungsdatum", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingRunSummaryInvoiceDto.prototype, "kartenIk", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingRunSummaryInvoiceDto.prototype, "cardInsurerName", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingRunSummaryInvoiceDto.prototype, "kostentraegerIk", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingRunSummaryInvoiceDto.prototype, "kostentraegerName", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingRunSummaryInvoiceDto.prototype, "leistungserbringerIk", void 0);
__decorate([
    Expose(),
    Type(() => GkvPapierannahmestelleDto),
    __metadata("design:type", Object)
], GkvBillingRunSummaryInvoiceDto.prototype, "papierannahmestelle", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvBillingRunSummaryInvoiceDto.prototype, "urbelegCount", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingRunSummaryInvoiceDto.prototype, "begleitzettelDocumentId", void 0);
/**
 * A produced Nutzdatendatei's **Lieferung** as the practice sees it
 * (TM-9636 / #1621).
 *
 * ⚠ **Only the state, never a reason.** A Lieferung waiting for a dispatch
 * address or a recipient key is called *"Wartet auf Versand"* here and nothing
 * else: that is not a technical problem a practice could judge or remedy, and
 * an error message about it would demand exactly that of them. The waiting
 * reasons stand in the Versand console (#1629).
 *
 * ⚠ **No Datenaustauschreferenz and no Transfernummer.** Both only come into
 * being at the Versandversuch (#1622); until then they do not exist, and a
 * field showing `null` would look like one somebody forgot to fill.
 *
 * ⚠ **A Verarbeitungsmeldung does not change the `status`** (#1625). It stands
 * beside it, because it is a different event: Ruf 1 says whether the file
 * arrived, Ruf 2 says something about its processing — and what that is Therame
 * does not interpret.
 */
export class GkvLieferungStatusDto {
    id;
    status;
    /** ISO timestamp — since when it is ready to send; its place in the queue. */
    firstReadyAt;
    /**
     * The **Verarbeitungsmeldungen** for this Lieferung, the oldest first
     * (TM-9636 / #1625).
     *
     * ⚠ **Empty means *"none came"*, not *"not yet"*.** A7 §3.2.3 explicitly
     * calls Ruf 2 *"eine Empfehlung"*; whether a Kasse sends one at all it
     * settles in its Fachverfahrensvereinbarung. A Lieferung without a
     * Verarbeitungsmeldung is the normal case and not an open matter.
     */
    verarbeitungsmeldungen;
    /**
     * What a **Versand operator** did to this Lieferung, the oldest action first
     * (TM-9636 / #1630).
     *
     * ⚠ **With a name and a reason, and that is the decision.** A Lieferung that
     * is *accepted* although no Rückmeldung could ever be matched to it has an
     * author; without them the practice would be left with the claim alone, and
     * its view would contradict itself as soon as it put the moment of acceptance
     * beside the absence of a Rückmeldung.
     *
     * ⚠ **Empty is the normal case.** The vast majority of Lieferungen match
     * themselves; only where a human intervened stands here.
     */
    operatorActs;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvLieferungStatusDto.prototype, "id", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvLieferungStatusDto.prototype, "status", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvLieferungStatusDto.prototype, "firstReadyAt", void 0);
__decorate([
    Expose(),
    Type(() => GkvVerarbeitungsmeldungDto),
    __metadata("design:type", Array)
], GkvLieferungStatusDto.prototype, "verarbeitungsmeldungen", void 0);
__decorate([
    Expose(),
    Type(() => VersandOperatorActDto),
    __metadata("design:type", Array)
], GkvLieferungStatusDto.prototype, "operatorActs", void 0);
/**
 * An **attachment** on a Verarbeitungsmeldung as the practice sees it
 * (TM-9636 / #1625).
 *
 * ⚠ **No field carries the content.** The practice fetches the bytes through a
 * separate call that authorises against its Standort; passing them along here
 * would mean putting the content of every attachment of every Lieferung into
 * every response of the progress view — and therefore into every log and every
 * cache that records a status response. A spec holds this promise down
 * (`gkv-lieferung-metadaten.spec.ts`).
 */
export class GkvVerarbeitungsmeldungAttachmentDto {
    id;
    /** The file name the message gave. */
    filename;
    /** The media type without parameters, e.g. `application/pdf`. */
    contentType;
    byteLength;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvVerarbeitungsmeldungAttachmentDto.prototype, "id", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvVerarbeitungsmeldungAttachmentDto.prototype, "filename", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvVerarbeitungsmeldungAttachmentDto.prototype, "contentType", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvVerarbeitungsmeldungAttachmentDto.prototype, "byteLength", void 0);
/**
 * A **Verarbeitungsmeldung** — Ruf 2 — on a Lieferung (TM-9636 / #1625).
 *
 * ⚠ **No state, no result, no interpretation.** What stands in the message A7
 * §3.2.3 leaves to the individual Fachverfahrensvereinbarung, and what the six
 * Kassenarten really send there is unmeasured
 * (`docs/wayfinder/1565-transport-ohne-dakota.md` §10.2). This response
 * therefore says only **that** something came, **from whom** and **when** — and
 * passes the attachments on. A human has to read them.
 *
 * ⚠ **The subject does not stand here either.** It repeats Absender IK,
 * recipient IK and Transfernummer; it tells the practice nothing its Lieferung
 * does not already say, and a Verarbeitungskennzeichen in it would look like a
 * result.
 */
export class GkvVerarbeitungsmeldungDto {
    id;
    /** Which point the message came from — the address from the `From` header. */
    absender;
    /** ISO timestamp — when it arrived. */
    receivedAt;
    attachments;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvVerarbeitungsmeldungDto.prototype, "id", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvVerarbeitungsmeldungDto.prototype, "absender", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvVerarbeitungsmeldungDto.prototype, "receivedAt", void 0);
__decorate([
    Expose(),
    Type(() => GkvVerarbeitungsmeldungAttachmentDto),
    __metadata("design:type", Array)
], GkvVerarbeitungsmeldungDto.prototype, "attachments", void 0);
/** One Nutzdatendatei of a run, as the status surface shows it. */
export class GkvBillingRunFileStatusDto {
    id;
    /** `UNB.S003 Empfänger`. */
    datenannahmestelleIk;
    /** Spec name: "Kassenart". */
    insurerType;
    /**
     * `UNB.0035`, in the run's vocabulary — `T` in Erprobung, `E` in Echtbetrieb,
     * `P` for a Prüfdatei of a test office (#1952).
     */
    testindikator;
    status;
    skipReason;
    errorMessage;
    /** How many Fälle this file carries. */
    caseCount;
    /**
     * `REC.Rechnungsart` — `1` Selbstabrechner, `2` abweichendes Zertifikats-IK.
     *
     * `null` on every file that was not produced. ⚠ It varies from file to file
     * within one run:
     * the same afternoon can produce a `1` for a practice's Physio file and a `2`
     * for its Ergo one, because the ITSG issues one certificate per IK and IK
     * Stellen 1–2 *are* the Berufsgruppe.
     */
    rechnungsart;
    /** The IK the file went out under. `null` until it was produced. */
    absenderIk;
    /**
     * `ESOL0007` — Stellen 1–8 of **both** filenames of the pair.
     *
     * ⚠ The client shows the stem, never one filename: the Nutzdatendatei and its
     * Auftragsdatei are `ESOL0007` and `ESOL0007.AUF`, and GGT Anlage 7 §3.2.1
     * forbids handing over one without the other.
     */
    filenameStem;
    /** `UNB.0020`, burned and never given back. */
    datenaustauschreferenz;
    /** Auftragssatz Stellen 25–27, burned and never given back. */
    /** Spec name: "Transfernummer". */
    transferNumber;
    /**
     * The Gesamtrechnungen this file went out with — empty until it was produced.
     *
     * ⚠ **Read off the frozen rows, never re-derived.** The Kostenträgerdatei
     * turns over 15 to 25 times a year; what is shown here is what the
     * Begleitzettel of March said, not what today's edition would say.
     */
    summaryInvoices;
    /**
     * This file's Lieferung — `null` under **Übergabe** (TM-9636 / #1621).
     *
     * ⚠ **`null` means *"on this route there is none"*, not *"not yet"*.** Under
     * Übergabe the practice gets the file pair in hand and sends it itself; nobody
     * reports back whether it did. A state for it would be a claim about a
     * process we do not see.
     */
    lieferung;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingRunFileStatusDto.prototype, "id", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingRunFileStatusDto.prototype, "datenannahmestelleIk", void 0);
__decorate([
    Expose()
    /** Spec name: "Kassenart". */
    ,
    __metadata("design:type", String)
], GkvBillingRunFileStatusDto.prototype, "insurerType", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingRunFileStatusDto.prototype, "testindikator", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingRunFileStatusDto.prototype, "status", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingRunFileStatusDto.prototype, "skipReason", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingRunFileStatusDto.prototype, "errorMessage", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvBillingRunFileStatusDto.prototype, "caseCount", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingRunFileStatusDto.prototype, "rechnungsart", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingRunFileStatusDto.prototype, "absenderIk", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingRunFileStatusDto.prototype, "filenameStem", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingRunFileStatusDto.prototype, "datenaustauschreferenz", void 0);
__decorate([
    Expose()
    /** Spec name: "Transfernummer". */
    ,
    __metadata("design:type", Object)
], GkvBillingRunFileStatusDto.prototype, "transferNumber", void 0);
__decorate([
    Expose(),
    Type(() => GkvBillingRunSummaryInvoiceDto),
    __metadata("design:type", Array)
], GkvBillingRunFileStatusDto.prototype, "summaryInvoices", void 0);
__decorate([
    Expose(),
    Type(() => GkvLieferungStatusDto),
    __metadata("design:type", Object)
], GkvBillingRunFileStatusDto.prototype, "lieferung", void 0);
/**
 * One §302 Abrechnungsfall of a run — one Verordnung.
 *
 * ⚠ Deselected Fälle appear here too, with `skipReason = 'abgewählt'`. That is
 * the whole reason the run records them: absence could not say whether a rule
 * refused the Verordnung or a human took it out.
 */
export class GkvBillingRunItemStatusDto {
    treatmentPlanId;
    /** `null` for a Fall that goes into no file, which is every set-aside Fall. */
    fileId;
    status;
    skipReason;
    skipDetail;
    errorMessage;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingRunItemStatusDto.prototype, "treatmentPlanId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingRunItemStatusDto.prototype, "fileId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingRunItemStatusDto.prototype, "status", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingRunItemStatusDto.prototype, "skipReason", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingRunItemStatusDto.prototype, "skipDetail", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingRunItemStatusDto.prototype, "errorMessage", void 0);
/**
 * One of the two ways a run's output leaves the house (TM-9540 / #1555).
 *
 * ⚠ **Display, never a barrier.** Under route (i) a human copies the files into
 * dakota's handover directory and carries the Urbelege to the post office, and
 * nobody reports either back. What stands here is exclusively what *we* have
 * seen — namely that somebody collected the bytes. A tick claiming completeness
 * would be worse than none at all.
 */
export class GkvBillingRunPickupDto {
    /**
     * How many artefacts there are to collect — file pairs or Begleitzettel PDFs.
     *
     * `0` means there is nothing to collect and is not an error: a run all of
     * whose files were skipped has neither bytes nor paper.
     */
    availableCount;
    /** How often it was fetched. `0` means "never yet", never "not asked". */
    downloadCount;
    /** ISO timestamp of the last fetch, or `null`. */
    lastDownloadedAt;
}
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvBillingRunPickupDto.prototype, "availableCount", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvBillingRunPickupDto.prototype, "downloadCount", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingRunPickupDto.prototype, "lastDownloadedAt", void 0);
/**
 * A run's **Versand release** as the practice sees it (TM-9636 / #1626).
 *
 * ⚠ **`null` under Übergabe.** There the practice gets its file pair in hand
 * and carries it to the Datenannahmestelle itself; there is nothing to release.
 * A block standing there with empty fields would look like a release somebody
 * forgot.
 *
 * The name comes from `pt_user` on read, like `executorFirstName` in the
 * Historie. The client has no store of names of its own; it only knew its own
 * and showed every other person a user id.
 */
export class GkvVersandFreigabeDto {
    releasedByUserId;
    releasedByFirstName;
    releasedByLastName;
    /** `TOTP` or `PASSKEY` — email and SMS one-time passwords never count (#1598). */
    factor;
    /** ISO moment — `auth_time`, that is when the factor was given. */
    at;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvVersandFreigabeDto.prototype, "releasedByUserId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvVersandFreigabeDto.prototype, "releasedByFirstName", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvVersandFreigabeDto.prototype, "releasedByLastName", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvVersandFreigabeDto.prototype, "factor", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvVersandFreigabeDto.prototype, "at", void 0);
/**
 * Response of `GET /v1/gkv-billing-run/:gkvBillingRunId/status` — what one run
 * did, or is doing (TM-9538 / #1519).
 */
export class GkvBillingRunStatusResponseDto {
    id;
    officeLocationId;
    status;
    /** ISO timestamp — the moment the operator pressed the button. */
    createdAt;
    /** ISO timestamp — null while still PENDING. */
    startedAt;
    /** ISO timestamp — null until COMPLETED / CANCELED / FAILED. */
    completedAt;
    /** ISO timestamp — the Stichtag the candidate list was assembled with. */
    cutoff;
    /**
     * A Probelauf — every Nutzdatendatei forced to `UNB.0035 = T` (#1523).
     *
     * ⚠ **The only thing that tells a Testlauf from a real run afterwards.** Both
     * carry the same statuses, produce files and burn the transport counters; what
     * differs is invisible on any file row — the Rechnungsnummern came from the
     * `gkv-test` series and no Anspruchszeile was written, so the Fälle are still
     * billable. Without this field a practice looking at its Historie in March
     * could not say which of two `COMPLETED` runs actually claimed anything.
     */
    isTestRun;
    /**
     * Who released this run, with which second factor and when — `null` under
     * Übergabe (TM-9636 / #1626).
     */
    versandFreigabe;
    files;
    items;
    totalCount;
    successCount;
    skippedCount;
    failedCount;
    pendingCount;
    /**
     * The digital pickup — the file-package ZIP (TM-9540 / #1555).
     *
     * {@link GkvBillingRunPickupDto.availableCount} counts the produced pairs,
     * not the files in the ZIP: a pair is two files, and they belong together.
     */
    digitalPickup;
    /**
     * The paper pickup — the Begleitzettel together with the Urbelege stacking
     * list (TM-9540 / #1554, #1555).
     *
     * ⚠ **Separate from the digital one, and that is the point.** File and paper
     * go to two different addresses, and §7(2) only starts the four-week period
     * with the arrival of *all* Abrechnungsunterlagen. A single "collected" line
     * would hide exactly the failure mode this slice is built against: the run
     * looks successful, and the money does not come.
     *
     * {@link GkvBillingRunPickupDto.availableCount} counts the **consignments**,
     * that is the distinct Begleitzettel PDFs — one per Belegannahmestelle, not
     * one per Gesamtrechnung.
     */
    paperPickup;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingRunStatusResponseDto.prototype, "id", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingRunStatusResponseDto.prototype, "officeLocationId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingRunStatusResponseDto.prototype, "status", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingRunStatusResponseDto.prototype, "createdAt", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingRunStatusResponseDto.prototype, "startedAt", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingRunStatusResponseDto.prototype, "completedAt", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingRunStatusResponseDto.prototype, "cutoff", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], GkvBillingRunStatusResponseDto.prototype, "isTestRun", void 0);
__decorate([
    Expose(),
    Type(() => GkvVersandFreigabeDto),
    __metadata("design:type", Object)
], GkvBillingRunStatusResponseDto.prototype, "versandFreigabe", void 0);
__decorate([
    Expose(),
    Type(() => GkvBillingRunFileStatusDto),
    __metadata("design:type", Array)
], GkvBillingRunStatusResponseDto.prototype, "files", void 0);
__decorate([
    Expose(),
    Type(() => GkvBillingRunItemStatusDto),
    __metadata("design:type", Array)
], GkvBillingRunStatusResponseDto.prototype, "items", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvBillingRunStatusResponseDto.prototype, "totalCount", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvBillingRunStatusResponseDto.prototype, "successCount", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvBillingRunStatusResponseDto.prototype, "skippedCount", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvBillingRunStatusResponseDto.prototype, "failedCount", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvBillingRunStatusResponseDto.prototype, "pendingCount", void 0);
__decorate([
    Expose(),
    Type(() => GkvBillingRunPickupDto),
    __metadata("design:type", GkvBillingRunPickupDto)
], GkvBillingRunStatusResponseDto.prototype, "digitalPickup", void 0);
__decorate([
    Expose(),
    Type(() => GkvBillingRunPickupDto),
    __metadata("design:type", GkvBillingRunPickupDto)
], GkvBillingRunStatusResponseDto.prototype, "paperPickup", void 0);
//# sourceMappingURL=gkv-billing-run-status.response.dto.js.map